import { is } from '../is.js';
import scopeLogger from 'lib/scope-logger.js';
import { px2em } from '../px2em/@impl/px2em.js';

const LANDSCAPE = 'landscape';
const PORTRAIT = 'portrait';

/**
 * Apply when calculate scale to fit schema screen to device screen.
 * It should prevent extra upscale.
 * It's not a "magic number", it 414/360, e.g. up to 115%
 * TODO: need more elegant solution for this problem, it need to use any landing on any device
 */
const MIN_SCALE = 0.86956;

/**
 * @param schema
 * @returns {schema}
 */
export function recalculatePositions(schema) {
  const console = scopeLogger('RMI:recalculate-position', false);
  const {
    originalSize,
    isRotatedViewEnabled = false, // deprecated
    widgets,
    slides,
  } = schema;

  // inject original size in every slide, to match container description
  slides.forEach((slide) => {
    Object.assign(slide, originalSize);
  });

  const [schemaPrimaryOrientation, schemaSecondaryOrientation] =
    originalSize.width > originalSize.height
      ? [LANDSCAPE, PORTRAIT]
      : [PORTRAIT, LANDSCAPE];

  schema.classes.forEach((classDesc) => {
    let { width: parentWidth, height: parentHeight } = classDesc.parent;
    const parentPercentWidth = parentWidth / 100;
    const parentPercentHeight = parentHeight / 100;
    const primarySize = {
      // remove 'orientation' to have 'placement' for both orientations
      // orientation: schemaPrimaryOrientation,
      width: CSS.percent(classDesc.width / parentPercentWidth),
      height: CSS.percent(classDesc.height / parentPercentHeight),
    };
    if ('x' in classDesc) {
      primarySize.left = CSS.percent(classDesc.x / parentPercentWidth);
      primarySize.x = CSS.px(classDesc.x);
    }
    if ('y' in classDesc) {
      primarySize.top = CSS.percent(classDesc.y / parentPercentHeight);
      primarySize.y = CSS.px(classDesc.y);
    }
    classDesc.placements = [primarySize];
    if ('fontSize' in classDesc) {
      classDesc.fontSize = px2em(classDesc.fontSize);
    }
  });

  // recalculate every widget size and position into relative values, add some derived.
  for (const widgetId in widgets) {
    const widget = widgets[widgetId];
    if (is.widget.buttonType(widget.parent)) {
      console.log('omit recalculate for nesting widget', widgetId);
      continue;
    }
    let { width, height } = widget.parent;

    const geometryCalculate = getWidgetGeometry(
      widgetPrimaryGeometry(widget),
      width,
      height,
    );
    /* no only containers, any widget on standard slide */
    const geometry = is.slideDisplayMode.standard(widget.parent)
      ? Object.assign(
          {},
          geometryCalculate.absoluteSize,
          geometryCalculate.relativePosition,
        )
      : Object.assign(
          {},
          geometryCalculate.relativeSize,
          geometryCalculate.relativePosition,
        );

    widget.placements = [
      {
        orientation: isRotatedViewEnabled
          ? schemaPrimaryOrientation
          : undefined,
        ...geometry,
      },
    ];

    // TODO: 'isRotatedViewEnabled' is deprecated
    if (isRotatedViewEnabled) {
      width = widget.parent.rotatedWidth || width;
      height = widget.parent.rotatedHeight || height;
      const secondaryGeometryCalculate = getWidgetGeometry(
        widgetSecondaryGeometry(widget),
        width,
        height,
      );
      /* no only containers, any widget on standard slide */
      const secondaryGeometry = is.slideDisplayMode.standard(widget.parent)
        ? {
            ...secondaryGeometryCalculate.absoluteSize,
            ...secondaryGeometryCalculate.relativePosition,
          }
        : {
            ...secondaryGeometryCalculate.relativeSize,
            ...secondaryGeometryCalculate.relativePosition,
          };
      widget.placements.push({
        orientation: schemaSecondaryOrientation,
        ...secondaryGeometry,
      });
    }

    // try to replace absolute font size
    if (widget.type === 'text') {
      /**
			 * Replace px value with em
			 * @example in
<div style="text-align: left;">
	<span style="font-size: 24px; line-height: 1; color: rgba(255, 255, 255, 1); font-weight: 500;  ">Нравится фильм?</span>
</div>
<div style="text-align: left;">
	<span style="font-size: 24px; line-height: 1; color: rgba(255, 255, 255, 1); font-weight: 500;  ">Смотрите похожий  со скидкой</span>
</div>
			 *
			 */

      try {
        widget.content = relativeFontSize(widget.content);
      } catch (error) {
        console.error('Failure on font adjust', error);
      }
    }
  }

  // adjust creative size to current device
  const {
    length: { main, cross, ...extras },
    orientation,
  } = getAdjustSize(schemaPrimaryOrientation, originalSize);

  schema.size = [
    {
      width: main,
      orientation: isRotatedViewEnabled ? schemaPrimaryOrientation : undefined,
      height: cross,
      ...extras,
    },
  ];
  // TODO: 'isRotatedViewEnabled' is deprecated
  if (isRotatedViewEnabled) {
    schema.size.push({
      orientation: orientation.secondary,
      width: cross,
      height: main,
    });
  }

  const vw = originalSize.width / 100;
  const vh = originalSize.height / 100;

  /**
   * recalculate font size base into vw/vh to properly fit text
   * exclusion: container's text
   * @see ../components/container/container.module.css
   */
  schema.fontSize = {
    byWidth: `${16 / vw}vw`,
    byHeight: `${16 / vh}vh`,
  };

  return schema;
}

function getAdjustSize(schemaPrimaryOrientation, originalSize) {
  const screen = {
    height: window.screen.height,
    width: window.screen.width,
  };
  const { matches: isPortrait } = window.matchMedia(
    `screen and (orientation: ${PORTRAIT})`,
  );

  if (schemaPrimaryOrientation === PORTRAIT && isPortrait) {
    const fitScale = Math.max(
      originalSize.width / screen.width,
      originalSize.height / screen.height,
    );
    return {
      fitScale,
      length: {
        main: originalSize.width / fitScale,
        cross: originalSize.height / fitScale,
        aspectRatio: `${originalSize.width}/${originalSize.height}`,
      },
      orientation: {
        primary: PORTRAIT,
        secondary: LANDSCAPE,
      },
    };
  } else if (schemaPrimaryOrientation === LANDSCAPE && isPortrait) {
    const fitScale = Math.max(
      originalSize.height / screen.width,
      originalSize.width / screen.height,
    );
    return {
      fitScale,
      length: {
        main: originalSize.width / fitScale,
        cross: originalSize.height / fitScale,
        aspectRatio: `${originalSize.width}/${originalSize.height}`,
      },
      orientation: {
        primary: LANDSCAPE,
        secondary: PORTRAIT,
      },
    };
  } else if (schemaPrimaryOrientation === PORTRAIT && false === isPortrait) {
    const fitScale = Math.max(
      MIN_SCALE,
      originalSize.height / screen.width,
      originalSize.width / screen.height,
    );
    return {
      fitScale,
      length: {
        main: originalSize.width / fitScale,
        cross: originalSize.height / fitScale,
        aspectRatio: `${originalSize.width}/${originalSize.height}`,
      },
      orientation: {
        primary: PORTRAIT,
        secondary: LANDSCAPE,
      },
    };
  } else if (schemaPrimaryOrientation === LANDSCAPE && false === isPortrait) {
    const widthScale = originalSize.width / screen.width;
    const heightScale = originalSize.height / screen.height;
    const fitScale = Math.max(widthScale, heightScale);

    return {
      fitScale,
      length: {
        // main: originalSize.width / fitScale,
        cross: originalSize.height / fitScale,
        aspectRatio: `${originalSize.width}/${originalSize.height}`,
        maxWidth: CSS.percent(100),
        maxHeight: CSS.percent(100),
      },
      orientation: {
        primary: LANDSCAPE,
        secondary: PORTRAIT,
      },
    };
  }
}

export function getWidgetGeometry(widget, mainAxis, crossAxis) {
  let mainAxisPercent = mainAxis / 100;
  let crossAxisPercent = crossAxis / 100;

  return {
    get absolutePosition() {
      return {
        top: CSS.px(widget.y),
        right: CSS.px(mainAxis - widget.x - widget.width),
        bottom: CSS.px(crossAxis - widget.y - widget.height),
        left: CSS.px(widget.x),
      };
    },
    get absoluteSize() {
      return {
        width: CSS.px(widget.width),
        height: CSS.px(widget.height),
      };
    },
    get relativePosition() {
      const props = [
        ['top', widget.y / crossAxisPercent],
        ['right', (mainAxis - widget.x - widget.width) / mainAxisPercent],
        ['bottom', (crossAxis - widget.y - widget.height) / crossAxisPercent],
        ['left', widget.x / mainAxisPercent],
      ].reduce(propsCollection(CSS.percent), {});

      return props;
    },
    get relativeSize() {
      return [
        ['width', widget.width / mainAxisPercent],
        ['height', widget.height / crossAxisPercent],
      ].reduce(propsCollection(CSS.percent), {});
    },
  };
}

function widgetPrimaryGeometry(widget) {
  return {
    x: widget.x,
    y: widget.y,
    width: widget.width,
    height: widget.height,
  };
}

/**
 * return widget geometry for secondary view,
 * fallback to properties from main view,
 * schema may not provide secondary geometry
 * in some case, maybe already fixed.
 * @param widget
 * @return {{x: *, width: *, y: *, height: *}}
 */
function widgetSecondaryGeometry(widget) {
  const {
    rotatedX: x = widget.x,
    rotatedY: y = widget.y,
    rotatedWidth: width = widget.width,
    rotatedHeight: height = widget.height,
  } = widget;

  return {
    x,
    y,
    width,
    height,
  };
}

function relativeFontSize(fragment) {
  return fragment.replace(
    /font-size:\s*(\d+)px/gi,
    (_substr, originFontSize /* matchedAt, origin*/) => {
      return `font-size: ${px2em(originFontSize)}`;
    },
  );
}

function propsCollection(propHolder) {
  return (obj, [name, value]) => {
    return Object.assign(obj, {
      get [name]() {
        try {
          return propHolder(value);
        } catch {
          return undefined;
        }
      },
    });
  };
}
